import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  IconButton,
  Popover,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { apiClient } from '../services/apiClient';
import { useCart } from '../services/CartContext';

export default function Hero({ onNavigate }) {
  const { addToCart, cartItems, updateQuantity, setIsCartOpen } = useCart();
  // Live countdown timer state
  const [timeLeft, setTimeLeft] = useState({ hours: 3, minutes: 58, seconds: 12 });

  // Stats counter values
  const [stats, setStats] = useState({ brands: 0, payout: 0, redeemed: 0 });

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deals, setDeals] = useState([]);
  const [error, setError] = useState(null);

  const [popupPage, setPopupPage] = useState(0);
  const [popupTotalPages, setPopupTotalPages] = useState(1);
  const [popupIsFirst, setPopupIsFirst] = useState(true);
  const [popupIsLast, setPopupIsLast] = useState(true);
  const [hoveredImage, setHoveredImage] = useState(null);

  const fetchDeals = async (pageNumber) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`/product-item/coupon-type/1?page=${pageNumber}&size=6`, { skipAuth: true });
      if (response && response.success && response.data) {
        setDeals(response.data.content || []);
        setPopupTotalPages(response.data.totalPages || 1);
        setPopupIsFirst(response.data.first);
        setPopupIsLast(response.data.last);
      } else {
        throw new Error(response?.message || 'Failed to fetch deals');
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Something went wrong while loading deals.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDeals = () => {
    setPopupPage(0);
    setOpen(true);
  };

  useEffect(() => {
    if (open) {
      fetchDeals(popupPage);
    }
  }, [popupPage, open]);

  const handleClose = () => {
    setOpen(false);
    setDeals([]);
    setPopupPage(0);
  };

  useEffect(() => {
    const targetBrands = 48;

    // Calculate 24-hr time-based dynamic counts for today
    const now = new Date();
    const todayStr = now.toLocaleDateString('en-CA'); // YYYY-MM-DD
    const secondsToday = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
    const dayProgress = secondsToday / 86400; // 0.0 to 1.0 throughout the 24-hour day

    // Base daily progression throughout 24 hours
    const basePayout = Math.floor(75000 + dayProgress * 310000);
    const baseRedeemed = Math.floor(350 + dayProgress * 1500);

    let targetPayout = basePayout;
    let targetRedeemed = baseRedeemed;

    try {
      const stored = localStorage.getItem('isoret_daily_stats');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && parsed.date === todayStr) {
          // On the same date, ensure counts NEVER decrease and increment slightly on each refresh
          const minPayout = (parsed.payout || basePayout) + Math.floor(Math.random() * 250 + 50);
          const minRedeemed = (parsed.redeemed || baseRedeemed) + Math.floor(Math.random() * 3 + 1);

          targetPayout = Math.max(basePayout, minPayout);
          targetRedeemed = Math.max(baseRedeemed, minRedeemed);
        }
      }
      // Persist today's stats to localStorage
      localStorage.setItem('isoret_daily_stats', JSON.stringify({
        date: todayStr,
        payout: targetPayout,
        redeemed: targetRedeemed,
      }));
    } catch (e) {
      console.error('LocalStorage stats error', e);
    }

    let brandsVal = 0;
    let payoutVal = 0;
    let redeemedVal = 0;

    const interval = setInterval(() => {
      let updated = false;
      if (brandsVal < targetBrands) {
        brandsVal += Math.ceil(targetBrands / 30);
        if (brandsVal > targetBrands) brandsVal = targetBrands;
        updated = true;
      }
      if (payoutVal < targetPayout) {
        payoutVal += Math.ceil((targetPayout - payoutVal) / 8) || 1;
        if (payoutVal > targetPayout) payoutVal = targetPayout;
        updated = true;
      }
      if (redeemedVal < targetRedeemed) {
        redeemedVal += Math.ceil((targetRedeemed - redeemedVal) / 8) || 1;
        if (redeemedVal > targetRedeemed) redeemedVal = targetRedeemed;
        updated = true;
      }

      setStats({
        brands: brandsVal,
        payout: payoutVal,
        redeemed: redeemedVal,
      });

      if (!updated) clearInterval(interval);
    }, 30);

    return () => clearInterval(interval);
  }, []);

  // Timer countdown logic
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        let { hours, minutes, seconds } = prev;
        if (seconds > 0) {
          seconds--;
        } else if (minutes > 0) {
          minutes--;
          seconds = 59;
        } else if (hours > 0) {
          hours--;
          minutes = 59;
          seconds = 59;
        } else {
          clearInterval(timer);
        }
        return { hours, minutes, seconds };
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTimer = () => {
    const h = String(timeLeft.hours).padStart(2, '0');
    const m = String(timeLeft.minutes).padStart(2, '0');
    const s = String(timeLeft.seconds).padStart(2, '0');
    return `Expires in ${h}:${m}:${s}`;
  };

  return (
    <Box
      component="section"
      id="top"
      sx={{
        padding: { xs: '70px 0 40px', md: '76px 0 40px' },
        position: 'relative',
        background: '#0F2E2B',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: `
            radial-gradient(600px 400px at 85% -10%, rgba(212,162,76,0.16), transparent 60%),
            radial-gradient(500px 400px at -10% 30%, rgba(47,143,118,0.22), transparent 60%)
          `,
          pointerEvents: 'none',
        },
      }}
    >
      <Box
        sx={{
          maxHeight: 'none',
          maxWidth: 1240,
          margin: '0 auto',
          padding: { xs: '0 20px', md: '0 32px' },
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.05fr 0.95fr' },
            gap: { xs: '40px', md: '56px' },
            alignItems: 'center',
            position: 'relative',
          }}
        >
          {/* Left Side: Headline Copy & Stats */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: { xs: 'center', md: 'flex-start' },
              textAlign: { xs: 'center', md: 'left' },
            }}
          >
            <Box
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                textTransform: 'uppercase',
                fontSize: '12px',
                letterSpacing: '0.14em',
                color: '#E8C078',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '22px',
                '&::before': {
                  content: '""',
                  width: '16px',
                  height: '1px',
                  backgroundColor: '#E8C078',
                  display: 'inline-block',
                },
              }}
            >
              No gambling · No betting · Assured only
            </Box>

            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '36px', sm: '48px', md: '58px' },
                lineHeight: 1.03,
                color: '#F5F1E6',
                marginBottom: '22px',
                fontFamily: '"Fraunces", serif',
                fontWeight: 600,
                letterSpacing: '-0.01em',
                '& em': {
                  fontStyle: 'italic',
                  color: '#E8C078',
                },
              }}
            >
              Every coupon<br />comes with a<br /><em>promise</em> attached.
            </Typography>

            <Typography
              sx={{
                fontSize: '17px',
                color: 'rgba(245,241,230,0.66)',
                maxWidth: '460px',
                mx: { xs: 'auto', md: 0 },
                mb: '32px',
                lineHeight: 1.5,
              }}
            >
              iSoret sells digital coupon cards — Bronze, Silver, Gold — each one backed by assured cashback or a branded reward. Buy the card, redeem real deals, watch the payout land. No spins, no chance.
            </Typography>

            <Box
              sx={{
                display: 'flex',
                gap: { xs: '8px', sm: '14px' },
                marginBottom: '44px',
                flexWrap: 'nowrap',
                justifyContent: { xs: 'center', md: 'flex-start' },
                width: '100%',
              }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={() => (onNavigate ? onNavigate('coupons') : (window.location.href = '/coupons'))}
                sx={{
                  flex: 1,
                  px: { xs: '12px', sm: '22px' },
                  py: { xs: '10px', sm: '12px' },
                  fontSize: { xs: '12px', sm: '14.5px' },
                  whiteSpace: 'nowrap',
                  boxShadow: '0 8px 20px -8px rgba(212,162,76,0.6)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 24px -8px rgba(212,162,76,0.75)',
                    backgroundColor: '#E8C078',
                  },
                }}
              >
                Browse coupon cards
              </Button>
              <Button
                variant="outlined"
                onClick={() => (onNavigate ? onNavigate('coupons', 'deals') : (window.location.href = '/coupons#deals'))}
                sx={{
                  flex: 1,
                  px: { xs: '12px', sm: '22px' },
                  py: { xs: '10px', sm: '12px' },
                  fontSize: { xs: '12px', sm: '14.5px' },
                  whiteSpace: 'nowrap',
                  borderColor: 'rgba(245, 241, 230, 0.14)',
                  color: '#F5F1E6',
                  '&:hover': {
                    borderColor: '#E8C078',
                    color: '#E8C078',
                  },
                }}
              >
                See today's deals &rarr;
              </Button>
            </Box>

            <Box
              sx={{
                display: 'flex',
                gap: '36px',
                flexWrap: 'wrap',
                justifyContent: { xs: 'center', md: 'flex-start' },
                width: '100%',
              }}
            >
              <Box>
                <Typography
                  component="b"
                  sx={{
                    display: 'block',
                    fontFamily: '"Fraunces", serif',
                    fontSize: '26px',
                    color: '#E8C078',
                    fontWeight: 600,
                  }}
                >
                  {stats.brands}
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    fontSize: '12.5px',
                    color: 'rgba(245,241,230,0.66)',
                    textTransform: 'uppercase',
                    letterSpacing: '.06em',
                  }}
                >
                  Partner brands
                </Typography>
              </Box>

              {/* <Box>
                <Typography
                  component="b"
                  sx={{
                    display: 'block',
                    fontFamily: '"Fraunces", serif',
                    fontSize: '26px',
                    color: '#E8C078',
                    fontWeight: 600,
                  }}
                >
                  ₹{stats.payout.toLocaleString()}
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    fontSize: '12.5px',
                    color: 'rgba(245,241,230,0.66)',
                    textTransform: 'uppercase',
                    letterSpacing: '.06em',
                  }}
                >
                  Cashback paid out
                </Typography>
              </Box> */}

              <Box>
                <Typography
                  component="b"
                  sx={{
                    display: 'block',
                    fontFamily: '"Fraunces", serif',
                    fontSize: '26px',
                    color: '#E8C078',
                    fontWeight: 600,
                  }}
                >
                  {stats.redeemed}
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    fontSize: '12.5px',
                    color: 'rgba(245,241,230,0.66)',
                    textTransform: 'uppercase',
                    letterSpacing: '.06em',
                  }}
                >
                  Coupons redeemed today
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Right Side: Welcome Coupon Card */}
          <Box
            sx={{
              position: 'relative',
              background: '#FAF3E6',
              borderRadius: '22px',
              boxShadow: '0 20px 50px -20px rgba(0,0,0,0.5)',
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              minHeight: '420px',
              color: '#0F2E2B',
              overflow: 'visible',
            }}
          >
            {/* Stub */}
            <Box
              sx={{
                width: { xs: '100%', sm: '32%' },
                background: 'linear-gradient(160deg, #1C6758, #153A35 120%)',
                borderRadius: { xs: '22px 22px 0 0', sm: '22px 0 0 22px' },
                padding: '26px 18px',
                display: 'flex',
                flexDirection: { xs: 'row', sm: 'column' },
                alignItems: 'center',
                justifyContent: 'space-between',
                position: 'relative',
              }}
            >
              <Box
                component="span"
                sx={{
                  writingMode: { xs: 'horizontal-tb', sm: 'vertical-rl' },
                  transform: { xs: 'none', sm: 'rotate(180deg)' },
                  fontFamily: '"JetBrains Mono", monospace',
                  letterSpacing: '0.22em',
                  fontSize: '11px',
                  color: 'rgba(245,241,230,0.7)',
                  textTransform: 'uppercase',
                }}
              >
                iSORET · ASSURED
              </Box>
              <Box
                sx={{
                  width: '82px',
                  height: '82px',
                  borderRadius: '50%',
                  backgroundColor: '#D4A24C',
                  color: '#0F2E2B',
                  display: 'grid',
                  placeItems: 'center',
                  textAlign: 'center',
                  fontFamily: '"Fraunces", serif',
                  fontWeight: 700,
                  fontSize: '11.5px',
                  lineHeight: 1.2,
                  boxShadow: '0 6px 20px -6px rgba(212,162,76,0.7)',
                  margin: { xs: 0, sm: '16px 0' },
                }}
              >
                STARTER<br />COUPON
              </Box>
              <Box
                component="span"
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '10px',
                  opacity: 0.7,
                  color: '#F5F1E6',
                }}
              >
                NO. 08823
              </Box>
            </Box>

            {/* Dashed Divider with Notches */}
            <Box
              sx={{
                position: 'relative',
                width: { xs: '100%', sm: 0 },
                height: { xs: 0, sm: 'auto' },
                borderLeft: { xs: 'none', sm: '2px dashed rgba(15,46,43,0.25)' },
                borderTop: { xs: '2px dashed rgba(15,46,43,0.25)', sm: 'none' },
              }}
            >
              {/* Notches for large layout */}
              <Box
                sx={{
                  display: { xs: 'none', sm: 'block' },
                  position: 'absolute',
                  top: '-11px',
                  left: '-11px',
                  width: '22px',
                  height: '22px',
                  borderRadius: '50%',
                  backgroundColor: '#0F2E2B',
                  zIndex: 2,
                }}
              />
              <Box
                sx={{
                  display: { xs: 'none', sm: 'block' },
                  position: 'absolute',
                  bottom: '-11px',
                  left: '-11px',
                  width: '22px',
                  height: '22px',
                  borderRadius: '50%',
                  backgroundColor: '#0F2E2B',
                  zIndex: 2,
                }}
              />
            </Box>

            {/* Main Coupon Area */}
            <Box
              sx={{
                flex: 1,
                padding: '28px 30px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              {/* Upper row content */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                  <Box
                    component="span"
                    sx={{
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: '11px',
                      letterSpacing: '.06em',
                      backgroundColor: '#0F2E2B',
                      color: '#F5F1E6',
                      padding: '4px 10px',
                      borderRadius: '999px',
                    }}
                  >
                    WELCOME OFFER
                  </Box>
                  <Box
                    sx={{
                      fontFamily: '"Fraunces", serif',
                      fontWeight: 800,
                      fontSize: '32px',
                      color: '#E4572E',
                      lineHeight: 1,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    ₹10
                  </Box>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '21px',
                    fontFamily: '"Fraunces", serif',
                    fontWeight: 700,
                    color: '#0F2E2B',
                    mb: 0.5,
                  }}
                >
                  Welcome Coupon!
                </Typography>
                <Typography
                  sx={{
                    fontSize: '13.5px',
                    fontWeight: 600,
                    color: '#1C6758',
                    mb: 1.8,
                  }}
                >
                  🎁 ₹10 Your First Scratch
                </Typography>

                {/* Bullet Points */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6, mt: 1 }}>
                  {[
                    'Digital Scratch Card',
                    'Upto 50₹ Cashback',
                    'Exciting Coupons and More',
                  ].map((text, idx) => (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Typography sx={{ color: '#1C6758', fontSize: '13px', fontWeight: 'bold', lineHeight: 1 }}>✔</Typography>
                      <Typography sx={{ color: '#0F2E2B', fontSize: '13px', fontWeight: 500, lineHeight: 1 }}>
                        {text}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Lower row actions */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  borderTop: '1px dashed rgba(15, 46, 43, 0.2)',
                  pt: 2,
                  mt: 2,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={handleOpenDeals}
                  sx={{
                    flex: 1,
                    borderColor: '#0F2E2B',
                    color: '#0F2E2B',
                    fontSize: '13px',
                    py: '9px',
                    '&:hover': {
                      borderColor: '#1C6758',
                      backgroundColor: 'rgba(15, 46, 43, 0.05)',
                    },
                  }}
                >
                  Deals
                </Button>
                {(() => {
                  const cartItem = cartItems.find((item) => item.id === 1);
                  if (cartItem) {
                    return (
                      <Box sx={{ display: 'flex', gap: '8px', flex: 1, alignItems: 'center' }}>
                        <Box
                          sx={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            backgroundColor: '#0F2E2B',
                            borderRadius: '999px',
                            padding: '2px 12px',
                            border: '1px solid rgba(15, 46, 43, 0.15)',
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => updateQuantity(1, cartItem.quantity - 1)}
                            sx={{
                              color: '#FAF3E6',
                              '&:hover': { backgroundColor: 'rgba(250, 243, 230, 0.08)' },
                            }}
                          >
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              color: '#D4A24C',
                              fontFamily: '"JetBrains Mono", monospace',
                              fontSize: '14px',
                            }}
                          >
                            {cartItem.quantity}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => updateQuantity(1, cartItem.quantity + 1)}
                            disabled={cartItem.quantity >= 3}
                            sx={{
                              color: '#FAF3E6',
                              '&:hover': { backgroundColor: 'rgba(250, 243, 230, 0.08)' },
                              '&.Mui-disabled': { color: 'rgba(250, 243, 230, 0.25)' },
                            }}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <IconButton
                          onClick={() => setIsCartOpen(true)}
                          sx={{
                            backgroundColor: '#0F2E2B',
                            color: '#E8C078',
                            border: '1px solid rgba(15, 46, 43, 0.15)',
                            borderRadius: '999px',
                            p: '8px',
                            '&:hover': {
                              backgroundColor: '#E8C078',
                              color: '#0F2E2B',
                            },
                          }}
                        >
                          <ShoppingCartIcon sx={{ fontSize: '18px' }} />
                        </IconButton>
                      </Box>
                    );
                  }
                  return (
                    <Button
                      variant="contained"
                      onClick={() => addToCart({
                        id: 1,
                        name: "Welcome Coupon",
                        price: 10,
                        tier: "Starter",
                        bg: "linear-gradient(135deg, #1C6758 0%, #153A35 120%)"
                      })}
                      sx={{
                        flex: 1,
                        backgroundColor: '#0F2E2B',
                        color: '#FAF3E6',
                        fontSize: '13px',
                        py: '9px',
                        fontWeight: 700,
                        '&:hover': {
                          backgroundColor: '#1C6758',
                        },
                      }}
                    >
                      Buy
                    </Button>
                  );
                })()}
              </Box>
            </Box>

            {/* Floating Badge Removed */}
          </Box>
        </Box>
      </Box>

      {/* Welcome/Starter Deals Popup Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        slotProps={{
          paper: {
            sx: {
              background: '#153A35',
              border: '1px solid rgba(245, 241, 230, 0.14)',
              color: '#F5F1E6',
              borderRadius: '24px',
              padding: { xs: '16px', md: '28px' },
              position: 'relative',
              boxShadow: '0 24px 50px rgba(0,0,0,0.6)',
            },
          },
        }}
      >
        {/* Header Block */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: '16px', mb: 3 }}>
          <Box>
            <DialogTitle
              sx={{
                p: 0,
                fontFamily: '"Fraunces", serif',
                fontSize: { xs: '24px', md: '30px' },
                fontWeight: 700,
                color: '#F5F1E6',
              }}
            >
              Welcome Card Deals
            </DialogTitle>
            <Typography sx={{ color: 'rgba(245, 241, 230, 0.66)', fontSize: '14.5px', mt: 1 }}>
              Claim these starter items when you purchase the Welcome coupon card.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'space-between', sm: 'flex-end' } }}>
            {/* Pagination Controls */}
            {deals.length > 0 && (
              <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'nowrap' }}>
                <IconButton
                  onClick={() => setPopupPage((prev) => prev - 1)}
                  disabled={popupIsFirst || loading}
                  size="small"
                  sx={{
                    border: '1px solid rgba(245, 241, 230, 0.14)',
                    color: '#F5F1E6',
                    '&:hover': {
                      backgroundColor: '#E8C078',
                      color: '#0F2E2B',
                      borderColor: '#E8C078',
                    },
                    '&.Mui-disabled': {
                      color: 'rgba(245, 241, 230, 0.22)',
                      borderColor: 'rgba(245, 241, 230, 0.06)',
                    },
                  }}
                >
                  <NavigateBeforeIcon fontSize="small" />
                </IconButton>
                <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', color: '#E8C078', fontSize: '12.5px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {popupPage + 1} / {popupTotalPages}
                </Typography>
                <IconButton
                  onClick={() => setPopupPage((prev) => prev + 1)}
                  disabled={popupIsLast || loading}
                  size="small"
                  sx={{
                    border: '1px solid rgba(245, 241, 230, 0.14)',
                    color: '#F5F1E6',
                    '&:hover': {
                      backgroundColor: '#E8C078',
                      color: '#0F2E2B',
                      borderColor: '#E8C078',
                    },
                    '&.Mui-disabled': {
                      color: 'rgba(245, 241, 230, 0.22)',
                      borderColor: 'rgba(245, 241, 230, 0.06)',
                    },
                  }}
                >
                  <NavigateNextIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
            <IconButton
              onClick={handleClose}
              sx={{
                color: 'rgba(245, 241, 230, 0.66)',
                border: '1px solid rgba(245, 241, 230, 0.12)',
                borderRadius: '50%',
                p: 1,
                '&:hover': {
                  color: '#E8C078',
                  borderColor: '#E8C078',
                  backgroundColor: 'rgba(232,192,120,0.06)',
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <DialogContent sx={{ p: 0, overflowY: 'auto', maxHeight: '60vh', minHeight: '280px', position: 'relative' }}>
          {error && (
            <Typography sx={{ color: '#E4572E', fontFamily: '"JetBrains Mono", monospace', textAlign: 'center', my: 4 }}>
              ⚠️ Error: {error}
            </Typography>
          )}

          {loading && deals.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '280px' }}>
              <CircularProgress sx={{ color: '#E8C078' }} />
            </Box>
          ) : deals.length === 0 ? (
            <Typography sx={{ color: 'rgba(245, 241, 230, 0.4)', textAlign: 'center', my: 6, fontStyle: 'italic' }}>
              No deals available for this tier at the moment.
            </Typography>
          ) : (
            <Box sx={{ position: 'relative', minHeight: '280px' }}>
              {loading && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(21, 58, 53, 0.55)',
                    backdropFilter: 'blur(2px)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 10,
                    borderRadius: '16px',
                  }}
                >
                  <CircularProgress sx={{ color: '#E8C078' }} />
                </Box>
              )}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: '20px',
                  opacity: loading ? 0.35 : 1,
                  transition: 'opacity 0.2s ease',
                }}
              >
                {deals.map((deal) => {
                  const dealImgUrl = deal.imageUrl || `https://placehold.co/300x200/153A35/E8C078?text=${encodeURIComponent(deal.name || 'Deal')}`;
                  return (
                    <Box
                      key={deal.id}
                      sx={{
                        background: '#0F2E2B',
                        border: '1px solid rgba(245, 241, 230, 0.08)',
                        borderRadius: '16px',
                        display: 'flex',
                        height: '130px',
                        overflow: 'hidden',
                        transition: 'border-color 0.2s',
                        '&:hover': {
                          borderColor: 'rgba(232, 192, 120, 0.3)',
                        },
                      }}
                    >
                      {/* Left Side Product Image - Hover to view full size */}
                      <Box
                        onMouseEnter={(e) => {
                          const imgUrl = deal.imageUrl || `https://placehold.co/400x300/153A35/E8C078?text=${encodeURIComponent(deal.name || 'Deal')}`;
                          setHoveredImage({ url: imgUrl, title: deal.name, anchorEl: e.currentTarget });
                        }}
                        onMouseLeave={() => setHoveredImage(null)}
                        sx={{
                          width: '40%',
                          flexShrink: 0,
                          position: 'relative',
                          backgroundColor: '#0B2220',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '6px',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s',
                          '&:hover': {
                            backgroundColor: '#123733',
                          },
                        }}
                      >
                        <Box
                          component="img"
                          src={dealImgUrl}
                          alt={deal.name}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = `https://placehold.co/300x200/153A35/E8C078?text=${encodeURIComponent(deal.name || 'Deal')}`;
                          }}
                          sx={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            width: 'auto',
                            height: 'auto',
                            objectFit: 'contain',
                          }}
                        />
                      </Box>

                      {/* Right Side Content Info */}
                      <Box
                        sx={{
                          width: '60%',
                          padding: '12px 16px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          textAlign: 'left',
                        }}
                      >
                        <Box>
                          <Typography
                            sx={{
                              fontFamily: '"JetBrains Mono", monospace',
                              fontSize: '10px',
                              color: '#E8C078',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                            }}
                          >
                            {deal.brand ? `${deal.categoryName} · ${deal.brand}` : deal.categoryName}
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{
                              fontFamily: '"Fraunces", serif',
                              fontSize: '14.5px',
                              fontWeight: 600,
                              color: '#F5F1E6',
                              mt: 0.5,
                              lineHeight: 1.25,
                            }}
                          >
                            {deal.name}
                          </Typography>
                        </Box>

                        {/* Price Comparison Foot */}
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-end',
                            borderTop: '1px dashed rgba(245, 241, 230, 0.1)',
                            pt: 1,
                          }}
                        >
                          <Box>
                            <Typography sx={{ fontSize: '9px', color: 'rgba(245, 241, 230, 0.4)', textTransform: 'uppercase' }}>
                              Product Price
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: '"Fraunces", serif',
                                fontSize: '12.5px',
                                color: 'rgba(245, 241, 230, 0.5)',
                                textDecoration: 'line-through',
                              }}
                            >
                              ₹{deal.marketProductValuePaise != null ? (deal.marketProductValuePaise / 100).toFixed(0) : (deal.marketPrice ? Number(deal.marketPrice).toFixed(0) : '0')}
                            </Typography>
                          </Box>

                          <Box sx={{ textAlign: 'right' }}>
                            <Typography sx={{ fontSize: '9px', color: '#E8C078', textTransform: 'uppercase', fontWeight: 600 }}>
                              iSoret Price
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: '"Fraunces", serif',
                                fontSize: '14.5px',
                                color: '#E8C078',
                                fontWeight: 700,
                              }}
                            >
                              ₹{deal.iSoretProductValuePaise != null ? (deal.iSoretProductValuePaise / 100).toFixed(0) : 10}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Full Size Image Mouse Hover Popover */}
      <Popover
        open={Boolean(hoveredImage?.anchorEl)}
        anchorEl={hoveredImage?.anchorEl}
        onClose={() => setHoveredImage(null)}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        disableRestoreFocus
        slotProps={{
          paper: {
            sx: {
              pointerEvents: 'none',
              background: '#0F2E2B',
              border: '2px solid #E8C078',
              borderRadius: '16px',
              padding: '12px',
              boxShadow: '0 24px 48px rgba(0,0,0,0.85)',
              maxWidth: '360px',
              zIndex: 1400,
            },
          },
        }}
        sx={{
          pointerEvents: 'none',
        }}
      >
        {hoveredImage && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box
              component="img"
              src={hoveredImage.url}
              alt={hoveredImage.title || 'Product Image'}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = `https://placehold.co/400x300/153A35/E8C078?text=${encodeURIComponent(hoveredImage.title || 'Deal')}`;
              }}
              sx={{
                maxWidth: '320px',
                maxHeight: '320px',
                width: '100%',
                height: 'auto',
                objectFit: 'contain',
                borderRadius: '10px',
                backgroundColor: '#153A35',
              }}
            />
            {hoveredImage.title && (
              <Typography
                sx={{
                  fontFamily: '"Fraunces", serif',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#F5F1E6',
                  mt: 1.5,
                  textAlign: 'center',
                }}
              >
                {hoveredImage.title}
              </Typography>
            )}
          </Box>
        )}
      </Popover>
    </Box>
  );
}
